/**
 * useIdentityVerifyOtp — Verify OTP via GraphQL federation mutation + create session
 *
 * Flow:
 * 1. Calls verifyIdentityOtpChallenge mutation through proxy → Auth Service
 *    → Returns Cognito tokens (Magento uses same tokens, no exchange needed)
 * 2. Calls signIn('identity-otp') with tokens → NextAuth authorize()
 *    → Creates Redis session, returns JWT cookie with sessionId
 * 3. After signIn, useSession() automatically updates.
 *
 * Uses codegen-generated hook with identity Apollo client (gateway endpoint).
 */
import { useCallback } from 'react';
import { signIn } from 'next-auth/react';
import { identityAuthStore$ } from '../../stores/identity-auth.store';
import { useVerifyIdentityOtpChallengeMutation } from '../../api/identity';
import { getIdentityClient } from '../../api/identity/identity-client';

interface IdentityVerifyOtpParams {
    otp: string;
}

interface IdentityVerifyOtpResult {
    success: boolean;
    message?: string;
    errorCode?: string;
}

export const useIdentityVerifyOtp = () => {
    const [verifyOtpChallenge] = useVerifyIdentityOtpChallengeMutation({
        client: getIdentityClient(),
    });

    return useCallback(
        async (params: IdentityVerifyOtpParams): Promise<IdentityVerifyOtpResult> => {
            const session = identityAuthStore$.challengeSession.get();
            const phoneNumber = identityAuthStore$.phoneNumber.get();

            if (!session || !phoneNumber) {
                return { success: false, message: 'No active OTP session', errorCode: 'NO_SESSION' };
            }

            try {
                identityAuthStore$.isLoading.set(true);
                identityAuthStore$.error.set(undefined);

                // 1. Verify OTP via codegen-generated mutation (identity client → gateway proxy)
                const { data, errors } = await verifyOtpChallenge({
                    variables: { session, phoneNumber, otp: params.otp },
                });

                if (errors?.length) {
                    const errorMessage = errors[0].message;
                    identityAuthStore$.error.set(errorMessage);
                    return { success: false, message: errorMessage, errorCode: 'GRAPHQL_ERROR' };
                }

                const result = data?.verifyIdentityOtpChallenge;

                if (!result?.success || !result.idpAccessToken) {
                    const message = result?.message || 'OTP verification failed';
                    identityAuthStore$.error.set(message);

                    // CHALLENGE_CONTINUE means wrong OTP — update session so user can retry
                    if (result?.errorCode === 'CHALLENGE_CONTINUE') {
                        // Access new session from result (field added to GraphQL schema)
                        const newSession = (result as Record<string, unknown>)?.session as string | undefined;
                        if (newSession) {
                            identityAuthStore$.challengeSession.set(newSession);
                        } else {
                            // No new session returned — Cognito exhausted retries.
                            // Do NOT redirect to EnterPhoneNumber. Let the OTP component
                            // handle this by locking input and showing max-attempts message.
                            // The user can wait for OTP expiry and resend.
                            identityAuthStore$.challengeSession.set(undefined);
                            return { success: false, message, errorCode: 'SESSION_EXHAUSTED' };
                        }
                    }

                    return { success: false, message, errorCode: result?.errorCode ?? undefined };
                }

                // 2. Sign in via NextAuth to create Redis session + httpOnly cookie.
                //    Magento token exchange happens server-side in authorize().
                //    OTP is always for an existing user (login or post-registration).
                const signInResult = await signIn('identity-otp', {
                    redirect: false,
                    cognitoAccessToken: result.idpAccessToken,
                    cognitoIdToken: result.idpIdToken || '',
                    cognitoRefreshToken: result.idpRefreshToken || '',
                    phone: phoneNumber,
                    userId: '',
                });

                if (signInResult?.error) {
                    identityAuthStore$.error.set(signInResult.error);
                    return { success: false, message: signInResult.error, errorCode: 'SIGN_IN_FAILED' };
                }

                // 3. Success — update store (without setting isAuthenticated)
                // NOTE: isAuthenticated is set by auth.provider after verifying session persistence
                identityAuthStore$.authenticationStep.set('Authenticated');
                identityAuthStore$.challengeSession.set(undefined);

                return { success: true };
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Network error';
                identityAuthStore$.error.set(message);
                return { success: false, message, errorCode: 'NETWORK_ERROR' };
            } finally {
                identityAuthStore$.isLoading.set(false);
            }
        },
        [verifyOtpChallenge]
    );
};
